<template>
  <Dialog ref="commentsDialog" :open="isOpen" @update:open="togglePopover">
    <DialogContent class="max-h-[90dvh] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 p-0">
      <DialogHeader class="p-6">
        <DialogTitle>{{ title }}</DialogTitle>
      </DialogHeader>
      <div ref="commentWrapper" class="overflow-y-auto px-6">
        <div class="mt-4 grid gap-y-5">
          <div v-for="comment in comments" :key="comment.id">
            <Comment
              ref="commentRef"
              :comment="comment.comment"
              :id="comment.id"
              :liked-by-me="comment.likedByMe"
              :name="comment.name"
              :like-count="comment.likeCount"
              :reply-count="comment.replyCount"
              :reply-to-id="comment.replyToId"
              :content-id="id"
              :avatar-url="comment.avatarUrl"
              :comments="repliedComments ?? []"
            />
          </div>
        </div>
      </div>
      <DialogFooter class="border-t border-black/10 px-6 pb-6 pt-4">
        <div class="flex w-full flex-col">
          <div
            v-if="!commentStore.isReportCommentActive && isReplyComment"
            class="flex w-full items-center justify-between pb-4"
          >
            <div>
              <span class="mb-1 block text-xs"
                ><span>Reply to </span>
                <span class="font-bold"> {{ commentStore.replyToCommentName }}</span></span
              >
              <span class="block text-xs">{{ commentStore.replyToComment }}</span>
            </div>
            <CircleX :size="18" @click="clearReplyData" class="cursor-pointer" />
          </div>
          <CommentReportForm v-show="commentStore.isReportCommentActive" />
          <CommentForm
            v-show="!commentStore.isReportCommentActive"
            ref="commentFormRef"
            v-model:comment="comment"
            :error="commentFormError"
            :is-sending="isSending"
            :button-label="isReplyComment && t('common.sendAReply')"
            @submit="postComment"
          />
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCommentStore } from '@stores/comment.store';
import type { ComponentPublicInstance } from 'vue';

// Interfaces
import type { CommentInterface } from './comment.interface';

// Types
type CommentFormInstance = ComponentPublicInstance & {
  focus: () => void;
};

// Components
import { Dialog, DialogHeader, DialogTitle, DialogContent, DialogFooter } from '@ui/dialog';
import Comment from '@/components/modules/Comment/Comment.vue';
import CommentForm from '@modules/Comment/CommentForm.vue';
import CommentReportForm from '@modules/Comment/CommentReportForm.vue';

// Icons
import { CircleX } from 'lucide-vue-next';

// Props
const props = defineProps<{
  contentId?: string;
  title?: string;
  id?: string;
}>();

// Composables
const { t } = useI18n();

// Stores
const commentStore = useCommentStore();

// Element refs
const commentFormRef = ref<CommentFormInstance | null>(null);
const commentWrapper = ref<HTMLElement | null>(null);
const commentRef = ref<HTMLElement | null>(null);

// Refs
const comment = ref('');
const commentFormError = ref('');
const isSending = ref(false);
const repliedComments = ref([] as CommentInterface[]);

// Models
const isOpen = defineModel<boolean>('modelValue', {
  required: true,
});

const comments = defineModel<CommentInterface[]>('comments', {
  required: true,
});

// Computeds
const isReplyComment = computed(() => commentStore.replyToId);

// Watchers
watch(isReplyComment, (value) => {
  if (value) {
    focusInput();
  }
});

watch(
  () => commentStore.commentsDialogScrollPosition,
  async (newVal) => {
    await nextTick();

    if (commentWrapper.value) {
      commentWrapper.value.scrollTo({
        top: newVal,
        behavior: 'smooth',
      });
    }
  },
);

// Methods
const focusInput = () => {
  if (commentFormRef.value) commentFormRef.value?.focus();
};

const clearReplyData = async () => {
  await commentStore.clearReplyData();
  focusInput();
};

const togglePopover = () => {
  isOpen.value = !isOpen.value;
};

const postComment = async () => {
  if (!comment.value) {
    commentFormError.value = t('errors.emptyComment');
    return;
  }

  isSending.value = true;

  await commentStore.postComment(
    props.id,
    commentStore.replyToId ? `@${commentStore.replyToCommentName} ` + comment.value : comment.value,
    commentStore.replyToId,
  );
  commentFormError.value = '';

  if (!commentStore.commentPostError) {
    if (commentStore.replyToId) {
      await commentStore.fetchComments(props.id, commentStore.replyToId);
      repliedComments.value = commentStore.commentData;
    } else {
      await commentStore.fetchComments(props.id);
      commentStore.storeCommentDialogScrollPosition(0);
      setTimeout(() => {
        commentStore.storeCommentDialogScrollPosition(1);
      }, 500);
      comments.value = commentStore.commentData;
    }
  }

  clearReplyData();

  isSending.value = false;
  comment.value = '';
};
</script>
